import { IonAccordion } from '@ionic/react';
import { IonicButton } from 'ionic-ui';
import React, { ComponentProps, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { StoreOfficialDocs } from '../../../../../../schemas/storeGetStartedSchema';
import z from 'zod';
import FileDropBox from '../../../../../../components/ui/FileDropBox/FileDropBox';
import AccordionHeader from '../../../../../../components/Dashboard/getting-started/accordion/AccordionHeader';
import SetupItem from '../../../../../../components/Dashboard/getting-started/accordion/SetupItem';
import DocumentStatusField, {
  UploadedDoc,
} from '../../../../../../components/Dashboard/getting-started/DocumentStatusField';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../../store/store';
import { FileTypeKey, FileTypeValue } from '../../../../../../types/FileType';

type Props = {
  onSave: () => Promise<void> | void;
} & ComponentProps<typeof IonAccordion>;

const supportedDocsFormat: Partial<Record<FileTypeKey, Array<FileTypeValue>>> =
  {
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'application/pdf': ['.pdf'],
  };

export default function OfficialDocsAccordion({ onSave, ...props }: Props) {
  const {
    control,
    formState: { isDirty },
  } = useFormContext<z.infer<typeof StoreOfficialDocs>>();

  const userProfile = useSelector((state: RootState) => state.userProfile);

  const docMap = useMemo<Record<string, UploadedDoc>>(() => {
    const map: Record<string, UploadedDoc> = {};
    (userProfile?.verification_documents || []).forEach((doc: any) => {
      if (doc?.name) map[doc.name] = doc as UploadedDoc;
    });
    return map;
  }, [userProfile?.verification_documents]);

  // Lock the save button if every required doc is approved or pending.
  const allLocked = useMemo(() => {
    const required = [
      'CAC_STATUS_REPORT',
      'CAC',
      'TAX_IDENTIFICATION_NUMBER',
      'PROOF_OF_ADDRESS',
      'MEMORANDUM_AND_ARTICLES',
    ];
    return required.every((key) => {
      const status = docMap[key]?.verification_status;
      return status === 'approved' || status === 'pending';
    });
  }, [docMap]);

  return (
    <IonAccordion value="Store-official-docs" {...props}>
      <AccordionHeader>Official Documents</AccordionHeader>
      <div
        slot="content"
        className="flex flex-col p-4 gap-4 bg-background dark:bg-background-accent"
      >
        <DocumentStatusField
          label="Upload CAC status report"
          required
          doc={docMap['CAC_STATUS_REPORT']}
        >
          <Controller
            control={control}
            name="CAC_STATUS_REPORT"
            render={({ field, fieldState }) => (
              <FileDropBox
                {...field}
                value={field.value}
                onChange={field.onChange}
                maxFiles={1}
                allowCropping={false}
                allowPreview
                acceptedTypes={supportedDocsFormat}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </DocumentStatusField>

        <DocumentStatusField
          label="Upload CAC certificate"
          required
          doc={docMap['CAC']}
        >
          <Controller
            control={control}
            name="CAC_CERT"
            render={({ field, fieldState }) => (
              <FileDropBox
                {...field}
                value={field.value}
                onChange={field.onChange}
                maxFiles={1}
                allowCropping={false}
                allowPreview
                acceptedTypes={supportedDocsFormat}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="CAC_NUMBER"
            render={({ field, fieldState }) => (
              <SetupItem
                {...field}
                label="Store CAC number*"
                fieldType="text"
                hint={fieldState.error?.message}
              />
            )}
          />
        </DocumentStatusField>

        <DocumentStatusField
          label="Store tax identification number (TIN)"
          required
          doc={docMap['TAX_IDENTIFICATION_NUMBER']}
        >
          <Controller
            control={control}
            name="TAX_IDENTIFICATION_NUMBER"
            render={({ field, fieldState }) => (
              <SetupItem
                {...field}
                label="Store tax identification number (TIN)*"
                fieldType="text"
                hint={fieldState.error?.message}
              />
            )}
          />
          <label className="medium-emphasis-text mb-1 mt-2 block text-sm">
            Upload TIN certificate (optional)
          </label>
          <Controller
            control={control}
            name="TIN_CERTIFICATE"
            render={({ field, fieldState }) => (
              <FileDropBox
                {...field}
                value={field.value}
                onChange={field.onChange}
                acceptedTypes={supportedDocsFormat}
                maxFiles={1}
                allowCropping={false}
                allowPreview
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </DocumentStatusField>

        <DocumentStatusField
          label="Upload Utility bill (proof of address)"
          required
          doc={docMap['PROOF_OF_ADDRESS']}
        >
          <Controller
            control={control}
            name="PROOF_OF_ADDRESS"
            render={({ field, fieldState }) => (
              <FileDropBox
                {...field}
                value={field.value}
                onChange={field.onChange}
                acceptedTypes={supportedDocsFormat}
                maxFiles={1}
                allowCropping={false}
                allowPreview
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="ADDRESS"
            render={({ field, fieldState }) => (
              <SetupItem
                {...field}
                label="Confirm address on proof of address*"
                fieldType="text"
                hint={fieldState.error?.message}
              />
            )}
          />
        </DocumentStatusField>

        <DocumentStatusField
          label="Upload memorandum and articles"
          doc={docMap['MEMORANDUM_AND_ARTICLES']}
        >
          <Controller
            control={control}
            name="MEMORANDUM_AND_ARTICLES"
            render={({ field, fieldState }) => (
              <FileDropBox
                {...field}
                value={field.value}
                onChange={field.onChange}
                maxFiles={1}
                acceptedTypes={supportedDocsFormat}
                allowPreview
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </DocumentStatusField>

        <div className="pt-2">
          <IonicButton
            type="button"
            onClick={onSave}
            disabled={!isDirty || allLocked}
            className="w-full rounded-sm"
          >
            Save
          </IonicButton>
        </div>
      </div>
    </IonAccordion>
  );
}
